import React, { useEffect, useState } from "react";
import { algorand } from './algo-client';
import algosdk, { Transaction } from 'algosdk';
import { PeraWalletConnect } from "@perawallet/connect";
import Confetti from 'react-confetti';
import Airtable from 'airtable';

const algodToken = "";
const algodServer = "https://mainnet-api.algonode.cloud";
const algodPort = "";
const algodClient = new algosdk.Algodv2(algodToken, algodServer, algodPort);

const indexerToken = "";
const indexerServer = "https://mainnet-idx.algonode.cloud";
const indexerPort = "";
const indexerClient = new algosdk.Indexer(indexerToken, indexerServer, indexerPort);

const RAFFLE_END_TIME_STRING = '2025-12-01T11:00:00.000+10:30';
const RAFFLE_WALLET = "GTAKGY64LESRSFFNYFAXJTACK5WSCOSNWTY62KVR2H76SHOYN5AJYAADGQ";
const TICKET_PRICE_IN_ALGO = 0.02;
const CURRENT_RAFFLE_NAME = "KNIThead 000";

// Update Airtable constants
const AIRTABLE_API_KEY = 'patGZ1hsFZ1A8n1cT.80b3949e981df0fa70c6047bf3d2bd091857d470e98807ba565dddaacb9a1a82';
const AIRTABLE_BASE_ID = 'appk7kq8zF8oZroQd';
const airtable = new Airtable({ apiKey: AIRTABLE_API_KEY }).base(AIRTABLE_BASE_ID);

// Add these constants
const LP_ASSET_ID = 2637139287;
const MIN_FROGGO_OPTIN = 1_000_000; // 1 FROGGO in microFROGGO
const FROG_MIN_BALANCE = 500_000_000_000; // 250,000 FROGGO in microFROGGO
const RANDOM_WINNERS_COUNT = 7;

// Add excluded addresses constant
const EXCLUDED_ADDRESSES = [
  "IDTP4MRALV4WPPGNOCYCZ5V3AD5PDKE6IFFFL6DBHEEQJF57IAYTC3F4A4",
  "BNFIREKGRXEHCFOEQLTX3PU5SUCMRKDU7WHNBGZA4SXPW42OAHZBP7BPHY",
];

// Add constants for Lotto
const CURRENT_LOTTO = "LOTTO13";
const LOTTO_TICKET_ALGO_PRICE = 0.05;
const LOTTO_BOOST_PRICES = {
  1.0: 0.0,
  1.8: 0.02,
  2.5: 0.04,
  3.5: 0.06,
  5.0: 0.08,
  7.0: 0.10
};

// Add constant for lotto end time (format: "2024-03-21T00:00:00Z")
const LOTTO_END_TIME = '2025-07-05T11:00:00.000+10:30';

// Update the Title component with glass effect and matching colors
const Title: React.FC<{ text: string; className?: string }> = ({ text, className = "" }) => {
  return (
    <div
      className={`glass-effect px-8 py-2 rounded-xl mb-4 ${className}`}
      style={{ color: '#374151' }} // This matches the gray text color used in the boxes
    >
      <h2 className="text-xl font-bold">{text}</h2>
    </div>
  );
};

// Add these interfaces at the top of your file
interface AssetBalance {
  address: string;
  amount: number;
  // Add other properties if needed
}

interface RaffleStats {
  address: string;
  nfdName?: string;
  totalTickets: number;
}

interface AlgorandTransaction {
  note: string;
  sender: string;
  'asset-transfer-transaction'?: {
    amount: number;
  };
}

interface IndexerResponse {
  'next-token'?: string;
  'current-round': number;
  transactions: Array<{
    note?: string;
    sender: string;
    'asset-transfer-transaction'?: {
      amount: number;
    };
  }>;
}

// Add interface for burn stats
interface BurnStats {
  address: string;
  nfdName?: string;
  totalBurned: number;
}

// Add new interface for Airtable records
interface AirtableRecord {
  address: string;
  nfdName?: string;
  amount: number;
  type: 'tickets' | 'burns';
}

// Add new interface for RainStats
interface RainStats {
  address: string;
  nfdName?: string;
  totalRained: number;
}

const App = () => {
  const [froggoPrice, setFroggoPrice] = useState<number | null>(null);
  const [burnedValue, setBurnedValue] = useState<number>(0);
  const [tickets, setTickets] = useState<number>(1);
  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const [totalTicketsSold, setTotalTicketsSold] = useState<number>(0);
  const [userTickets, setUserTickets] = useState<number>(0);
  const [timeRemaining, setTimeRemaining] = useState<string>("00:00:00");
  const [raffleEnded, setRaffleEnded] = useState<boolean>(false);
  const [marketCap, setMarketCap] = useState<number | null>(null);
  const [burnedFroggo, setBurnedFroggo] = useState<number>(0);
  const [burnPercentage, setBurnPercentage] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [peraWallet] = useState(() =>
    new PeraWalletConnect({ shouldShowSignTxnToast: false })
  );
  const [rainAmount, setRainAmount] = useState<number>(100000);
  const [isRaining, setIsRaining] = useState<boolean>(false);
  const [holderAddresses, setHolderAddresses] = useState<string[]>([]);
  const [topRafflers, setTopRafflers] = useState<RaffleStats[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [topBurners, setTopBurners] = useState<BurnStats[]>([]);
  const [isLoadingBurns, setIsLoadingBurns] = useState(false);
  const MIN_RAIN_AMOUNT = 100000;
  const [rainProgress, setRainProgress] = useState<string>('');
  const [isGettingTickets, setIsGettingTickets] = useState(false);
  const [isLandscape, setIsLandscape] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [topRainMakers, setTopRainMakers] = useState<RainStats[]>([]);
  const [isLoadingRain, setIsLoadingRain] = useState(false);
  const [totalHolders, setTotalHolders] = useState<number>(0);
  const [burnAmount, setBurnAmount] = useState<number>(1000);
  const [isBurning, setIsBurning] = useState<boolean>(false);
  const [boostMultiplier, setBoostMultiplier] = useState<number>(1.0);
  const [lottoProgress, setLottoProgress] = useState<number>(0);
  const LOTTO_GOAL = 20_000_000;
  const [froggoBalance, setFroggoBalance] = useState<number | null>(null);
  const [lottoBoostMultiplier, setLottoBoostMultiplier] = useState<number>(1.0);
  const [selectedRainPreset, setSelectedRainPreset] = useState<number>(0);
  const [lottoPrize, setLottoPrize] = useState<number>(0);
  const [lottoTimeRemaining, setLottoTimeRemaining] = useState<string>("");
  const [lottoEnded, setLottoEnded] = useState<boolean>(false);
  const [isGettingLottoTickets, setIsGettingLottoTickets] = useState<boolean>(false);
  const [lottoTickets, setLottoTickets] = useState(1);
  const [raffleTickets, setRaffleTickets] = useState(1);  // Rename existing tickets state
  const [totalLottoTicketsSold, setTotalLottoTicketsSold] = useState(0);
  const [userLottoTickets, setUserLottoTickets] = useState(0);

  const MIN_HOLDER_BALANCE = 500_000; 

  // Replace single isGettingTickets state with two separate states
  const [isGettingRaffleTickets, setIsGettingRaffleTickets] = useState(false);

  useEffect(() => {
    const fetchFroggoPrice = async () => {
      try {
        // Use new Vestige Labs API to get Froggo price
        const response = await fetch(
          "https://api.vestigelabs.org/assets/price?asset_ids=2637111113&network_id=0&denominating_asset_id=0"
        );
        const data = await response.json();
        
        // Extract price from the new API response format
        const froggoData = data[0]; // First (and only) item in the array
        const froggoPrice = Number(froggoData.price); // Ensure it's a proper number
        
        setFroggoPrice(froggoPrice);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching Froggo price:", error);
        setLoading(false);
      }
    };

    fetchFroggoPrice();
  }, []);

  const fetchRaffleEntries = async () => {
    if (!RAFFLE_WALLET) return;

    try {
      // Get current time and 24 hours ago (changed to 4 weeks)
      const now = new Date();
      const twentyFourHoursAgo = new Date(now.getTime() - (28 * 24 * 60 * 60 * 1000));

      // Use indexer to fetch transactions
      const response = await indexerClient
        .searchForTransactions()
        .address(RAFFLE_WALLET)
        .assetID(2637111113) // $FROGGO asset ID
        .afterTime(twentyFourHoursAgo.toISOString())
        .do();

      let totalTickets = 0;
      let userTicketCount = 0;

      response.transactions.forEach((txn: any) => {
        try {
          // Decode the note from base64
          const noteBase64 = txn.note;
          if (!noteBase64) return;

          const noteDecoded = new TextDecoder().decode(Buffer.from(noteBase64, 'base64'));

          // Check if the note contains the current raffle name
          if (noteDecoded.includes(CURRENT_RAFFLE_NAME)) {
            // Extract ticket count using regex
            const ticketMatch = noteDecoded.match(/- (\d+) tickets/);
            if (ticketMatch && ticketMatch[1]) {
              const ticketAmount = parseInt(ticketMatch[1]);

              totalTickets += ticketAmount;

              if (txn.sender === walletAddress) {
                userTicketCount += ticketAmount;
              }
            }
          }
        } catch (error) {
          console.error("Error processing transaction:", error);
        }
      });

      setTotalTicketsSold(totalTickets);
      setUserTickets(userTicketCount);
    } catch (error) {
      console.error("Error fetching raffle entries:", error);
    }
  };

  useEffect(() => {
    const fetchEntries = () => {
      fetchRaffleEntries();
    };

    const interval = setInterval(fetchEntries, 5000); // Update every 20 seconds
    fetchEntries(); // Initial fetch

    return () => clearInterval(interval);
  }, [walletAddress]);

  const fetchLottoEntries = async () => {
    if (!RAFFLE_WALLET) return;
  
    try {
      // Get current time and 14 days ago
      const now = new Date();
      const eightDaysAgo = new Date(now.getTime() - (14 * 24 * 60 * 60 * 1000));
  
      // Use indexer to fetch transactions
      const response = await indexerClient
        .searchForTransactions()
        .address(RAFFLE_WALLET)
        .assetID(2637111113) // $FROGGO asset ID
        .afterTime(eightDaysAgo.toISOString())
        .do();
  
      let totalTickets = 0;
      let userTicketCount = 0;
  
      response.transactions.forEach((txn: any) => {
        try {
          // Decode the note from base64
          const noteBase64 = txn.note;
          if (!noteBase64) return;
  
          const noteDecoded = new TextDecoder().decode(Buffer.from(noteBase64, 'base64'));
  
          // Check if the note contains the current lotto name
          if (noteDecoded.includes(CURRENT_LOTTO)) {
            // Extract ticket count using regex
            const ticketMatch = noteDecoded.match(/- (\d+) tickets/);
            if (ticketMatch && ticketMatch[1]) {
              const ticketAmount = parseInt(ticketMatch[1]);
  
              totalTickets += ticketAmount;
  
              if (txn.sender === walletAddress) {
                userTicketCount += ticketAmount;
              }
            }
          }
        } catch (error) {
          console.error("Error processing transaction:", error);
        }
      });
  
      setTotalLottoTicketsSold(totalTickets);
      setUserLottoTickets(userTicketCount);
    } catch (error) {
      console.error("Error fetching lotto entries:", error);
    }
  };

  useEffect(() => {
    const fetchLottoEntriesWrapper = () => {
      fetchLottoEntries();
    };
  
    const interval = setInterval(fetchLottoEntriesWrapper, 5000); // Update every 5 seconds
    fetchLottoEntriesWrapper(); // Initial fetch
  
    return () => clearInterval(interval);
  }, [walletAddress]);

  const handleConnectPera = async () => {
    try {
      const accounts = await peraWallet.connect();
      setWalletAddress(accounts[0]);
    } catch (error) {
      console.error("Failed to connect Pera Wallet:", error);
    }
  };

  const disconnectPera = async () => {
    peraWallet.disconnect();
    setWalletAddress(null);
  };

  const fetchBurnedFroggoFromIndexer = async () => {
    try {
      const mainnetIndexerClient = new algosdk.Indexer(
        "", // No token required for AlgoNode
        "https://mainnet-idx.algonode.cloud",
        ""
      );

      const burnWallet = "BNFIREKGRXEHCFOEQLTX3PU5SUCMRKDU7WHNBGZA4SXPW42OAHZBP7BPHY";
      const assetId = 2637111113; // $FROGGO Asset ID

      type AssetResponse = {
        assets: {
          "asset-id": number;
          amount: number;
          creator?: string;
          "is-frozen"?: boolean;
        }[];
        "next-token"?: string;
      };

      let nextToken: string | null = null;
      let froggoAsset = null;

      // Paginate through all assets
      do {
        const response = (await mainnetIndexerClient
          .lookupAccountAssets(burnWallet)
          .nextToken(nextToken || "")
          .do()) as AssetResponse;

        // Search for $FROGGO in the current page
        froggoAsset = response.assets.find((asset) => asset["asset-id"] === assetId);

        if (froggoAsset) break; // Stop pagination if asset is found

        nextToken = response["next-token"] ?? null; // Use null if undefined
      } while (nextToken);

      if (!froggoAsset) {
        console.error("Burn wallet does not hold the $FROGGO asset.");
        return;
      }

      // Convert balance to human-readable format
      const burnedBalance = froggoAsset.amount / 1e6; // Assuming 6 decimals for $FROGGO
      const totalSupply = 1_000_000_000; // 1 billion supply
      const burnPercentage = ((burnedBalance / totalSupply) * 100).toFixed(2);

      // Update the state
      setBurnedFroggo(burnedBalance);
      setBurnPercentage(Number(burnPercentage));

      // Use new Vestige Labs API to get current price
      const response = await fetch(
        "https://api.vestigelabs.org/assets/price?asset_ids=2637111113&network_id=0&denominating_asset_id=0"
      );
      const data = await response.json();
      const froggoData = data[0]; // First (and only) item in the array
      const currentPrice = froggoData.price;

      const valueInAlgo = (burnedBalance * currentPrice).toFixed(2);
      setBurnedValue(Number(valueInAlgo));

    } catch (error) {
      console.error("Error fetching burned $FROGGO:", error);
    }
  };

  useEffect(() => {
    const updateTimer = () => {
      try {
        // Get current Adelaide time
        const adelaideTime = new Date().toLocaleString("en-US", { timeZone: "Australia/Adelaide" });
        const currentTimeDate = new Date(adelaideTime);

        // Create end time in Adelaide timezone
        const endTimeAdelaide = new Date(RAFFLE_END_TIME_STRING).toLocaleString("en-US", {
          timeZone: "Australia/Adelaide"
        });
        const endTimeDate = new Date(endTimeAdelaide);

        const currentMs = currentTimeDate.getTime();
        const endMs = endTimeDate.getTime();

        const timeLeft = endMs - currentMs;

        if (timeLeft <= 0) {
          setTimeRemaining("00:00:00");
          setRaffleEnded(true);
          return;
        }

        const seconds = Math.floor((timeLeft / 1000) % 60);
        const minutes = Math.floor((timeLeft / 1000 / 60) % 60);
        const hours = Math.floor((timeLeft / (1000 * 60 * 60)));

        setTimeRemaining(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
        setRaffleEnded(false);
      } catch (error) {
        console.error("Timer error:", error);
      }
    };

    const timer = setInterval(updateTimer, 1000);
    updateTimer();
    return () => clearInterval(timer);
  }, []);

  const enterRaffle = async (tickets: number) => {
    if (!walletAddress || !froggoPrice || !roundedFroggoPerTicket) return;  // Added roundedFroggoPerTicket check

    try {
      setIsGettingRaffleTickets(true);  // Changed to raffle-specific state
      const suggestedParams = await algodClient.getTransactionParams().do();

      // Calculate FROGGO payment amount based on base tickets (not boosted)
      const totalAmount = tickets * roundedFroggoPerTicket * 1e6; // Convert to microFROGGO
      const burnAmount = Math.floor(totalAmount * 0.3); // 30% to burn
      const raffleAmount = totalAmount - burnAmount; // 70% to raffle

      // Apply boost multiplier only to the ticket count in the note
      const boostedTickets = Math.ceil(tickets * boostMultiplier);

      // Create transaction for raffle wallet (with boosted ticket count in note)
      const raffleTxn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
        from: walletAddress,
        to: RAFFLE_WALLET,
        assetIndex: 2637111113,
        amount: raffleAmount,
        note: new TextEncoder().encode(`${CURRENT_RAFFLE_NAME} - ${boostedTickets} tickets`),
        suggestedParams,
      });

      // Create transaction for burn wallet (without note)
      const burnTxn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
        from: walletAddress,
        to: "BNFIREKGRXEHCFOEQLTX3PU5SUCMRKDU7WHNBGZA4SXPW42OAHZBP7BPHY",
        assetIndex: 2637111113,
        amount: burnAmount,
        suggestedParams,
      });

      // Calculate ALGO amount for boost (if boost is active)
      let txns = [raffleTxn, burnTxn];
      if (boostMultiplier > 1.0) {
        const algoAmount = Math.floor(tickets * getBoostAlgoPrice() * 1e6); // Convert to microALGO
        const algoTxn = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
          from: walletAddress,
          to: RAFFLE_WALLET,
          amount: algoAmount,
          suggestedParams,
        });
        txns.push(algoTxn);
      }

      // Group the transactions
      algosdk.assignGroupID(txns);

      // Sign all transactions
      const txnsToSign = txns.map(txn => ({ txn, signers: [walletAddress] }));
      const signedTxns = await peraWallet.signTransaction(txnsToSign.map(t => [t]));

      // Send all transactions
      await algodClient.sendRawTransaction(signedTxns).do();

      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 6000);

      // Refresh raffle data
      await fetchRaffleEntries();
    } catch (error) {
      console.error("Error entering raffle:", error);
      alert("Failed to enter raffle");
    } finally {
      setIsGettingRaffleTickets(false);  // Changed to raffle-specific state
    }
  };

  // Add to existing useEffect for initial data loading
  useEffect(() => {
    fetchBurnedFroggoFromIndexer();
    fetchLottoProgress();
  }, [walletAddress]);

  useEffect(() => {
    peraWallet.reconnectSession()
      .then((accounts) => {
        if (accounts.length) {
          setWalletAddress(accounts[0]);
        }
        peraWallet.connector?.on("disconnect", disconnectPera);
      })
      .catch((error) => {
        console.error("Failed to reconnect session:", error);
      });
  }, []);

  // Update the fetch function with balance check
  const fetchFroggoHolders = async () => {
    try {
      const response = await indexerClient.lookupAssetBalances(2637111113)
        .currencyGreaterThan(MIN_HOLDER_BALANCE * 1_000_000) // Convert to microFROGGO
        .do();
      
      const addresses = response.balances.map((balance: AssetBalance) => balance.address);
      setHolderAddresses(addresses);
      return addresses;
    } catch (error) {
      console.error("Error fetching FROGGO holders:", error);
      return [];
    }
  };

  // Add function to get total holder count
  const fetchTotalHolders = async () => {
    try {
      const response = await indexerClient.lookupAssetBalances(2637111113)
        .currencyGreaterThan(0)
        .do();
      
      setTotalHolders(response.balances.length);
    } catch (error) {
      console.error("Error fetching total holders:", error);
    }
  };

  // Update useEffect to fetch both counts
  useEffect(() => {
    fetchFroggoHolders();
    fetchTotalHolders();
  }, []);

  // Update the rain function with proper typing
  const handleRain = async () => {
    if (!walletAddress || rainAmount < MIN_RAIN_AMOUNT) return;

    try {
      setIsRaining(true);

      // Get all holder addresses
      const holders: string[] = holderAddresses.length ? holderAddresses : await fetchFroggoHolders();

      // Calculate whole number amount per holder (using Math.floor to remove decimals)
      const amountPerHolder = Math.floor(rainAmount / holders.length);

      if (amountPerHolder < 1) {
        alert("Rain amount too small to distribute to all holders");
        setIsRaining(false);
        return;
      }

      // Get network parameters for transactions
      const suggestedParams = await algodClient.getTransactionParams().do();

      // Create array of transaction groups, where each group contains a single transaction
      const txnGroups = holders.map((holder: string) => {
        const txn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
          from: walletAddress,
          to: holder,
          amount: amountPerHolder * 1_000_000, // Multiply by 1 million for correct decimals
          assetIndex: 2637111113,
          suggestedParams: suggestedParams,
          note: new TextEncoder().encode(`${holders.length} holders received ${amountPerHolder} FROGGO each with the lilfroggo.vercel.app rain feature!`)
        });

        return [{ txn, signers: [walletAddress] }];
      });

      // Get transactions signed by wallet
      // Note: we're passing an array of transaction groups
      const signedTxnGroups = await peraWallet.signTransaction(txnGroups);

      // Send each transaction group
      for (const signedTxnGroup of signedTxnGroups) {
        await algodClient.sendRawTransaction(signedTxnGroup).do();
      }

      alert(`Successfully rained ${rainAmount} FROGGO across ${holders.length} holders!`);
    } catch (error) {
      console.error("Error during rain:", error);
      alert("Failed to make it rain! Check console for details.");
    } finally {
      setIsRaining(false);
    }
  };

  // Update the fetch function
  const fetchRaffleStats = async (): Promise<RaffleStats[]> => {
    try {
      const response = await indexerClient
        .searchForTransactions()
        .address(RAFFLE_WALLET)
        .do() as IndexerResponse;

      const walletStats = new Map<string, number>();

      for (const txn of response.transactions) {
        try {
          if (!txn.note) continue;
          const note = new TextDecoder().decode(Buffer.from(txn.note, 'base64'));
          const ticketMatch = note.match(/(\d+)\s+tickets?/i);
          if (ticketMatch) {
            const tickets = parseInt(ticketMatch[1]);
            const sender = txn.sender;
            walletStats.set(sender, (walletStats.get(sender) || 0) + tickets);
          }
        } catch (error) {
          console.error("Error processing transaction:", error);
          continue;
        }
      }

      const sortedStats = Array.from(walletStats.entries())
        .map(([address, totalTickets]) => ({
          address,
          totalTickets
        }))
        .sort((a, b) => b.totalTickets - a.totalTickets)
        .slice(0, 3);

      return sortedStats;
    } catch (error) {
      console.error("Error fetching raffle stats:", error);
      return [];
    }
  };

  // Update the fetch function
  const fetchBurnStats = async (): Promise<BurnStats[]> => {
    try {
      const response = await indexerClient
        .searchForTransactions()
        .address("BNFIREKGRXEHCFOEQLTX3PU5SUCMRKDU7WHNBGZA4SXPW42OAHZBP7BPHY")
        .assetID(2637111113)
        .do() as IndexerResponse;

      const burnStats = new Map<string, number>();

      for (const txn of response.transactions) {
        try {
          if (!txn["asset-transfer-transaction"]?.amount) continue;
          const amount = txn["asset-transfer-transaction"].amount / 1_000_000; // Convert from microFROGGO
          const sender = txn.sender;
          burnStats.set(sender, (burnStats.get(sender) || 0) + amount);
        } catch (error) {
          console.error("Error processing burn transaction:", error);
          continue;
        }
      }

      const sortedBurns = Array.from(burnStats.entries())
        .map(([address, totalBurned]) => ({
          address,
          totalBurned
        }))
        .sort((a, b) => b.totalBurned - a.totalBurned)
        .slice(0, 3);

      return sortedBurns;
    } catch (error) {
      console.error("Error fetching burn stats:", error);
      return [];
    }
  };

  // Update the fetchRainStats function
  const fetchRainStats = async (): Promise<RainStats[]> => {
    try {
      const rainStats = new Map<string, number>();
      let nextToken: string | undefined = undefined;
      let hasMore = true;

      while (hasMore) {
        const response = await indexerClient
          .searchForTransactions()
          .assetID(2637111113)
          .limit(1000)
          .nextToken(nextToken || '')
          .do() as IndexerResponse;

        for (const txn of response.transactions) {
          try {
            if (!txn.note || !txn["asset-transfer-transaction"]) continue;

            const note = new TextDecoder().decode(Buffer.from(txn.note, 'base64'));

            // Only log and process if it contains "rain"
            if (note.toLowerCase().includes('rain')) {

              const amount = txn["asset-transfer-transaction"].amount / 1_000_000;
              const sender = txn.sender;
              rainStats.set(sender, (rainStats.get(sender) || 0) + amount);
            }
          } catch (error) {
            console.error("Error processing transaction:", error);
            continue;
          }
        }

        nextToken = response['next-token'];
        hasMore = !!nextToken;
        console.log("Has more transactions:", hasMore);
      }

      const sortedRains = Array.from(rainStats.entries())
        .map(([address, totalRained]) => ({
          address,
          totalRained
        }))
        .sort((a, b) => b.totalRained - a.totalRained)
        .slice(0, 3);

      return sortedRains;
    } catch (error) {
      console.error("Error fetching rain stats:", error);
      return [];
    }
  };

  // Update the fetchLeaderboardFromAirtable function
  const fetchLeaderboardFromAirtable = async () => {
    try {
      setIsLoadingStats(true);
      setIsLoadingBurns(true);
      setIsLoadingRain(true);

      // Fetch ticket stackers
      const ticketRecords: RaffleStats[] = [];
      await airtable('TicketStackers').select({
        sort: [{ field: 'totalTickets', direction: 'desc' }],
        maxRecords: 3,
      }).eachPage((records, fetchNextPage) => {
        records.forEach(record => {
          ticketRecords.push({
            address: record.get('address') as string,
            totalTickets: record.get('totalTickets') as number,
          });
        });
        fetchNextPage();
      });

      // Fetch burners
      const burnRecords: BurnStats[] = [];
      await airtable('Burners').select({
        sort: [{ field: 'totalBurned', direction: 'desc' }],
        maxRecords: 3,
      }).eachPage((records, fetchNextPage) => {
        records.forEach(record => {
          burnRecords.push({
            address: record.get('address') as string,
            totalBurned: record.get('totalBurned') as number,
          });
        });
        fetchNextPage();
      });

      // Fetch rain makers
      const rainRecords: RainStats[] = [];
      await airtable('RainMakers').select({
        sort: [{ field: 'totalRained', direction: 'desc' }],
        maxRecords: 3,
      }).eachPage((records, fetchNextPage) => {
        records.forEach(record => {
          rainRecords.push({
            address: record.get('address') as string,
            totalRained: record.get('totalRained') as number,
          });
        });
        fetchNextPage();
      });

      // Add NFD lookups for all records
      const ticketRecordsWithNFD = await Promise.all(
        ticketRecords.map(async (record) => {
          try {
            const nfdResponse = await fetch(`https://api.nf.domains/nfd/lookup?address=${record.address}`);
            const nfdData = await nfdResponse.json();
            return {
              ...record,
              nfdName: nfdData[record.address]?.name
            };
          } catch {
            return record;
          }
        })
      );

      const burnRecordsWithNFD = await Promise.all(
        burnRecords.map(async (record) => {
          try {
            const nfdResponse = await fetch(`https://api.nf.domains/nfd/lookup?address=${record.address}`);
            const nfdData = await nfdResponse.json();
            return {
              ...record,
              nfdName: nfdData[record.address]?.name
            };
          } catch {
            return record;
          }
        })
      );

      const rainRecordsWithNFD = await Promise.all(
        rainRecords.map(async (record) => {
          try {
            const nfdResponse = await fetch(`https://api.nf.domains/nfd/lookup?address=${record.address}`);
            const nfdData = await nfdResponse.json();
            return {
              ...record,
              nfdName: nfdData[record.address]?.name
            };
          } catch {
            return record;
          }
        })
      );

      // Update state with fetched data including NFDs
      setTopRafflers(ticketRecordsWithNFD);
      setTopBurners(burnRecordsWithNFD);
      setTopRainMakers(rainRecordsWithNFD);
    } catch (error) {
      console.error('Error fetching from Airtable:', error);
    } finally {
      setIsLoadingStats(false);
      setIsLoadingBurns(false);
      setIsLoadingRain(false);
    }
  };

  // Update the updateAirtableLeaderboard function
  const updateAirtableLeaderboard = async () => {
    try {
      setIsRefreshing(true);

      // Prompt for admin API key
      const adminApiKey = prompt('Secret refresh passcode for required:');
      if (!adminApiKey) {
        alert('Admin API KEY is required');
        return;
      }

      // Create a new temporary Airtable instance with the admin API key
      const adminAirtable = new Airtable({ apiKey: adminApiKey }).base(AIRTABLE_BASE_ID);

      // Fetch fresh data
      const raffleStats = await fetchRaffleStats();
      const burnStats = await fetchBurnStats();
      const rainStats = await fetchRainStats();

      // Update TicketStackers table using the admin Airtable instance
      const existingTicketRecords = await adminAirtable('TicketStackers').select().all();
      const deleteTicketPromises = existingTicketRecords.map(record =>
        adminAirtable('TicketStackers').destroy(record.id)
      );
      await Promise.all(deleteTicketPromises);

      const createTicketPromises = raffleStats.map(stat =>
        adminAirtable('TicketStackers').create({
          address: stat.address,
          totalTickets: stat.totalTickets,
        })
      );
      await Promise.all(createTicketPromises);

      // Update Burners table
      const existingBurnRecords = await adminAirtable('Burners').select().all();
      const deleteBurnPromises = existingBurnRecords.map(record =>
        adminAirtable('Burners').destroy(record.id)
      );
      await Promise.all(deleteBurnPromises);

      const createBurnPromises = burnStats.map(stat =>
        adminAirtable('Burners').create({
          address: stat.address,
          totalBurned: stat.totalBurned,
        })
      );
      await Promise.all(createBurnPromises);

      // Update RainMakers table
      const existingRainRecords = await adminAirtable('RainMakers').select().all();
      const deleteRainPromises = existingRainRecords.map(record =>
        adminAirtable('RainMakers').destroy(record.id)
      );
      await Promise.all(deleteRainPromises);

      const createRainPromises = rainStats.map(stat =>
        adminAirtable('RainMakers').create({
          address: stat.address,
          totalRained: stat.totalRained,
        })
      );
      await Promise.all(createRainPromises);

      // After all updates are done, fetch fresh data using the original read-only instance
      await fetchLeaderboardFromAirtable();

    } catch (error) {
      console.error('Error updating Airtable:', error);
      alert('Failed to update leaderboard. Please check your API key and try again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Update useEffect to fetch from Airtable on mount
  useEffect(() => {
    fetchLeaderboardFromAirtable();
  }, []);

  // Add the burn handler function
  const handleBurn = async () => {
    if (!walletAddress || burnAmount < 1000) return;

    try {
      setIsBurning(true);
      const suggestedParams = await algodClient.getTransactionParams().do();

      const totalAmount = burnAmount * 1e6; // Convert to microFROGGO
      const reserveAmount = Math.floor(totalAmount * 0.2); // 20% to reserve
      const burnAmount80 = totalAmount - reserveAmount; // 80% to burn

      // Create transaction for burn wallet (80%)
      const burnTxn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
        from: walletAddress,
        to: "BNFIREKGRXEHCFOEQLTX3PU5SUCMRKDU7WHNBGZA4SXPW42OAHZBP7BPHY",
        assetIndex: 2637111113,
        amount: burnAmount80,
        note: new TextEncoder().encode(`${burnAmount} FROGGO burned for 40 mil rain!`),
        suggestedParams,
      });

      // Create transaction for reserve wallet (20%)
      const reserveTxn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
        from: walletAddress,
        to: RAFFLE_WALLET,
        assetIndex: 2637111113,
        amount: reserveAmount,
        suggestedParams,
      });

      // Group the transactions
      const txns = [burnTxn, reserveTxn];
      algosdk.assignGroupID(txns);

      // Sign all transactions
      const txnsToSign = txns.map(txn => ({ txn, signers: [walletAddress] }));
      const signedTxns = await peraWallet.signTransaction(txnsToSign.map(t => [t]));

      // Send all transactions
      await algodClient.sendRawTransaction(signedTxns).do();
      await fetchLottoProgress(); // Refresh the progress

      alert(`Successfully burned ${burnAmount} FROGGO!`);
      setBurnAmount(1000); // Reset to minimum
    } catch (error) {
      console.error("Error burning FROGGO:", error);
      alert("Failed to burn FROGGO");
    } finally {
      setIsBurning(false);
    }
  };

  // Update the froggoPerTicket calculation
  const froggoPerTicket = froggoPrice ? 
    Math.floor((TICKET_PRICE_IN_ALGO / froggoPrice) * 1e6) / 1e6 : null;

  // Round down to nearest even number
  const roundedFroggoPerTicket = froggoPerTicket ? 
    Math.floor(froggoPerTicket / 2) * 2 : null;

  console.log("Debug - froggoPrice:", froggoPrice); // Debug log
  console.log("Debug - froggoPerTicket:", froggoPerTicket); // Debug log
  console.log("Debug - roundedFroggoPerTicket:", roundedFroggoPerTicket); // Debug log
  console.log("Debug - TICKET_PRICE_IN_ALGO:", TICKET_PRICE_IN_ALGO); // Debug log
  console.log("Debug - raffleTickets:", raffleTickets); // Debug log

  // Add function to handle boost click
  const handleBoostClick = () => {
    const multipliers = [1.0, 1.8, 2.5, 3.5, 5.0, 7.0];
    const currentIndex = multipliers.indexOf(boostMultiplier);
    const nextIndex = (currentIndex + 1) % multipliers.length;
    setBoostMultiplier(multipliers[nextIndex]);
  };

  // Add function to get boost button color
  const getBoostColor = () => {
    const colors = {
      1.0: 'bg-[#1c79b2]',
      1.8: 'bg-[#21a685]',
      2.5: 'bg-[#76d168]',
      3.5: 'bg-[#e8c61b]',
      5.0: 'bg-[#f1832c]',
      7.0: 'bg-[#f04f3b]'
    };
    return colors[boostMultiplier as keyof typeof colors];
  };

  // Add function to get the ALGO price based on boost level
  const getBoostAlgoPrice = () => {
    const prices = {
      1.0: 0.0,
      1.8: 0.01,
      2.5: 0.02,
      3.5: 0.03,
      5.0: 0.04,
      7.0: 0.05
    };
    return prices[boostMultiplier as keyof typeof prices];
  };

  // Add function to fetch burn progress
  const fetchLottoProgress = async () => {
    try {
      const response = await indexerClient
        .searchForTransactions()
        .address("BNFIREKGRXEHCFOEQLTX3PU5SUCMRKDU7WHNBGZA4SXPW42OAHZBP7BPHY")
        .assetID(2637111113)
        .do();

      let totalBurned = 0;
      for (const txn of response.transactions) {
        try {
          if (!txn.note) continue;
          const note = new TextDecoder().decode(Buffer.from(txn.note, 'base64'));
          if (note.includes('40 mil rain')) {
            const amount = txn["asset-transfer-transaction"].amount / 1_000_000; // Convert from microFROGGO
            totalBurned += amount;
          }
        } catch (error) {
          console.error("Error processing transaction:", error);
          continue;
        }
      }
      setLottoProgress(totalBurned);
    } catch (error) {
      console.error("Error fetching lotto progress:", error);
    }
  };

  const fetchFroggoBalance = async (address: string) => {
    try {
      const accountInfo = await indexerClient
        .lookupAccountAssets(address)
        .assetId(2637111113)
        .do();
      
      const assetHolding = accountInfo.assets.find((asset: any) => asset['asset-id'] === 2637111113);
      const balance = assetHolding ? assetHolding.amount / 1_000_000 : 0;
      setFroggoBalance(Math.floor(balance));
    } catch (error) {
      console.error("Error fetching FROGGO balance:", error);
      setFroggoBalance(null);
    }
  };

  useEffect(() => {
    if (walletAddress) {
      fetchFroggoBalance(walletAddress);
      // Set up interval to refresh balance
      const interval = setInterval(() => {
        fetchFroggoBalance(walletAddress);
      }, 5000); // Update every 5 seconds
      return () => clearInterval(interval);
    } else {
      setFroggoBalance(null);
    }
  }, [walletAddress]);

  // Update the getQualifiedHolders function, specifically the LP case
  const getQualifiedHolders = async (preset: number) => {
    try {
      let qualified: string[] = [];
      
      // Get all FROGGO holders first
      const response = await indexerClient
        .lookupAssetBalances(2637111113)
        .limit(1000)
        .do();

      switch (preset) {
        case 1: // Farmer (just opted in with > 1 FROGGO)
          qualified = response.balances
            .filter((b: AssetBalance) => b.amount >= MIN_FROGGO_OPTIN)
            .map((b: AssetBalance) => b.address);
          break;

        case 2: // Frog (over 250k)
          qualified = response.balances
            .filter((b: AssetBalance) => b.amount >= FROG_MIN_BALANCE)
            .map((b: AssetBalance) => b.address);
          break;

        case 3: // LP holders (just need LP token)
          // First get LP holders
          const lpHolders = (await indexerClient
            .lookupAssetBalances(LP_ASSET_ID)
            .do()).balances
            .filter((b: AssetBalance) => 
              b.amount > 0 && !EXCLUDED_ADDRESSES.includes(b.address)
            );

          // Then check each for FROGGO opt-in
          const qualifiedHolders = new Set();  // Use Set to ensure uniqueness
          for (const holder of lpHolders) {
            try {
              const froggoBalance = await indexerClient
                .lookupAccountAssets(holder.address)
                .assetId(2637111113)  // FROGGO asset ID
                .do();
              
              // If they have FROGGO asset in their account, they're opted in
              if (froggoBalance.assets && froggoBalance.assets.length > 0) {
                qualifiedHolders.add(holder.address);
              }
            } catch (error) {
              console.error(`Error checking FROGGO opt-in for ${holder.address}:`, error);
              continue;
            }
          }
          
          qualified = Array.from(qualifiedHolders) as string[];
          break;

        case 4: // Random 7 from holders over 250k
          const eligibleHolders = response.balances
            .filter((b: AssetBalance) => b.amount >= FROG_MIN_BALANCE)
            .map((b: AssetBalance) => b.address);
          
          // Shuffle and take first 7
          qualified = eligibleHolders
            .sort(() => Math.random() - 0.5)
            .slice(0, RANDOM_WINNERS_COUNT);
          break;
      }

      return qualified;
    } catch (error) {
      console.error("Error getting qualified holders:", error);
      return [];
    }
  };

  // Update the rain preset button click handlers
  const handleRainPresetClick = async (preset: number) => {
    setSelectedRainPreset(preset);
    const qualified = await getQualifiedHolders(preset);
    setHolderAddresses(qualified);
  };

  // Update useEffect to set initial frog preset
  useEffect(() => {
    handleRainPresetClick(2); // Set initial frog preset
  }, []);

  // Add helper function to get the recipient text
  const getRecipientText = (preset: number) => {
    switch (preset) {
      case 1:
        return "All holders";
      case 2:
        return "500k+ holders";
      case 3:
        return "LP providers";
      case 4:
        return "7 random holders";
      default:
        return "Qualified holders";
    }
  };

  // Add function to calculate lotto prize pool
  const calculateLottoPrize = async () => {
    try {
      const now = new Date();
      const eightDaysAgo = new Date(now.getTime() - (14 * 24 * 60 * 60 * 1000));

      const response = await indexerClient
        .searchForTransactions()
        .address(RAFFLE_WALLET)
        .assetID(2637111113) // $FROGGO asset ID
        .afterTime(eightDaysAgo.toISOString())
        .do();

      let totalAmount = 0;
      for (const txn of response.transactions) {
        try {
          if (!txn.note) continue;
          const note = new TextDecoder().decode(Buffer.from(txn.note, 'base64'));
          if (note.startsWith(CURRENT_LOTTO)) {
            totalAmount += txn["asset-transfer-transaction"].amount;
          }
        } catch (error) {
          console.error("Error processing transaction:", error);
          continue;
        }
      }
      
      return Math.floor(totalAmount * 0.8 / 1_000_000); // Convert from microFROGGO and apply 80%
    } catch (error) {
      console.error("Error calculating prize pool:", error);
      return 0;
    }
  };

  // Add useEffect to fetch prize pool
  useEffect(() => {
    const fetchPrize = async () => {
      const prize = await calculateLottoPrize();
      setLottoPrize(prize);
    };
    fetchPrize();
    const interval = setInterval(fetchPrize, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);

  // Add function to handle lotto boost click
  const handleLottoBoostClick = () => {
    const multipliers = [1.0, 1.8, 2.5, 3.5, 5.0, 7.0];
    const currentIndex = multipliers.indexOf(lottoBoostMultiplier);
    const nextIndex = (currentIndex + 1) % multipliers.length;
    setLottoBoostMultiplier(multipliers[nextIndex]);
  };

  // Add function to get lotto boost button color
  const getLottoBoostColor = () => {
    const colors = {
      1.0: 'bg-[#1c79b2]',
      1.8: 'bg-[#21a685]',
      2.5: 'bg-[#76d168]',
      3.5: 'bg-[#e8c61b]',
      5.0: 'bg-[#f1832c]',
      7.0: 'bg-[#f04f3b]'
    };
    return colors[lottoBoostMultiplier as keyof typeof colors];
  };

  // Add function to get the ALGO price based on lotto boost level
  const getLottoBoostAlgoPrice = () => {
    return LOTTO_BOOST_PRICES[lottoBoostMultiplier as keyof typeof LOTTO_BOOST_PRICES];
  };

  // Add useEffect for lotto countdown
  useEffect(() => {
    const updateLottoTime = () => {
      const now = new Date().getTime();
      const endTime = new Date(LOTTO_END_TIME).getTime();
      const distance = endTime - now;

      if (distance < 0) {
        setLottoTimeRemaining("Lotto Ended");
        setLottoEnded(true);
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setLottoTimeRemaining(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    };

    const timer = setInterval(updateLottoTime, 1000);
    updateLottoTime(); // Initial call

    return () => clearInterval(timer);
  }, []);

  // Add function to handle lotto entry
  const enterLotto = async (tickets: number) => {
    if (!walletAddress || !froggoPrice) return;

    try {
      setIsGettingLottoTickets(true);  // Changed to lotto-specific state
      const suggestedParams = await algodClient.getTransactionParams().do();
      
      // Calculate total FROGGO needed for tickets
      const froggoPerTicket = Math.floor((LOTTO_TICKET_ALGO_PRICE / froggoPrice) / 2) * 2;
      const totalFroggo = froggoPerTicket * tickets;
      const totalAmount = totalFroggo * 1e6; // Convert to microFROGGO

      // Create FROGGO transaction
      const froggoTxn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
        from: walletAddress,
        to: RAFFLE_WALLET,
        assetIndex: 2637111113,
        amount: totalAmount,
        note: new TextEncoder().encode(
          `${CURRENT_LOTTO} - ${lottoTickets + Math.ceil(lottoTickets * (lottoBoostMultiplier - 1))} tickets`
        ),
        suggestedParams,
      });

      // If using boost, add ALGO transaction
      let transactions = [froggoTxn];
      if (lottoBoostMultiplier > 1.0) {
        const algoTxn = algosdk.makePaymentTxnWithSuggestedParamsFromObject({
          from: walletAddress,
          to: RAFFLE_WALLET,
          amount: Math.floor(LOTTO_BOOST_PRICES[lottoBoostMultiplier as keyof typeof LOTTO_BOOST_PRICES] * tickets * 1e6),
          suggestedParams,
        });
        transactions.push(algoTxn);
      }

      // Group transactions if there's more than one
      if (transactions.length > 1) {
        algosdk.assignGroupID(transactions);
      }

      // Sign all transactions
      const txnsToSign = transactions.map(txn => ({ txn, signers: [walletAddress] }));
      const signedTxns = await peraWallet.signTransaction(txnsToSign.map(t => [t]));

      // Send transactions
      await algodClient.sendRawTransaction(signedTxns).do();

      // Show success message
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 6000);
      alert(`Entered the Froggo Lotto with ${lottoTickets + Math.ceil(lottoTickets * (lottoBoostMultiplier - 1))} tickets!`);

    } catch (error) {
      console.error("Error entering lotto:", error);
      alert("Failed to enter lotto. Please try again.");
    } finally {
      setIsGettingLottoTickets(false);  // Changed to lotto-specific state
    }
  };

  return (
    <div className="min-h-screen w-full overflow-auto pb-20">
      {/* Fixed Header with Connect Button */}
      <div className="fixed top-0 left-0 right-0 p-4 bg-[#1f1f1f] z-10">
        <button
          onClick={walletAddress ? disconnectPera : handleConnectPera}
          className="w-full bg-green-500 text-white font-bold py-2 px-6 rounded-lg border-2 border-white hover:bg-green-600"
        >
          {walletAddress ? `${walletAddress.slice(0, 5)}... connected` : "Connect Wallet"}
        </button>
        
        <div className="text-center text-white mt-2 font-bold">
          {froggoBalance !== null ? 
            `Your FROGGO: ${Math.floor(froggoBalance).toLocaleString()}` : 
            'Connect wallet to view balance'
          }
        </div>
      </div>

      {/* Boxes Container - adjusted padding */}
      <div className="w-full pt-32 px-4 pb-20">
        <div className="flex flex-col items-center space-y-8">
          {/* Fun Box Container */}
          <div className="relative flex flex-col items-center w-full max-w-[384px]">
            {/* Fun Box */}
            <div className="backdrop-blur-lg bg-white/30 rounded-xl p-8 w-full min-h-[600px] mb-4 shadow-[0_0_15px_rgba(255,255,255,0.8)]" style={{ 
              background: 'rgba(255, 255, 255, 0.6)',
              boxShadow: '0 0 15px rgba(255, 255, 255, 0.3), inset 0 0 15px rgba(255, 255, 255, 0.1)'
            }}>
              {/* Rain Section */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold text-black">Rain</h3>
                  <div className="flex-grow mx-4">
                    <input
                      type="text"
                      value={rainAmount.toString()}
                      onChange={(e) => {
                        const val = e.target.value.replace(/^0+/, '');
                        setRainAmount(val === '' ? 0 : Number(val));
                        setSelectedRainPreset(0);
                      }}
                      onBlur={(e) => {
                        const value = Math.max(MIN_RAIN_AMOUNT, Number(e.target.value));
                        setRainAmount(value);
                      }}
                      className="w-full p-2 rounded border text-center font-bold"
                      min={MIN_RAIN_AMOUNT}
                      step={1000}
                      placeholder={`min ${MIN_RAIN_AMOUNT.toLocaleString()}`}
                    />
                  </div>
                  <button
                    onClick={handleRain}
                    disabled={!walletAddress || rainAmount < MIN_RAIN_AMOUNT || isRaining}
                    className="bg-blue-500 p-2 rounded-md flex items-center justify-center hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer"
                    style={{ width: '40px', height: '40px' }}
                  >
                    <span role="img" aria-label="rain" className="cursor-pointer">🌧️</span>
                  </button>
                </div>

                <div className="text-center text-black font-bold">
                  {holderAddresses.length || "..."} / {totalHolders || "..."} qualified holders
                </div>

                <div className="text-center text-black mb-2 text-sm">  {/* Changed from text-gray-600 */}
                  {getRecipientText(selectedRainPreset)} will receive {Math.floor(rainAmount / (holderAddresses.length || 1))} FROGGO
                </div>

                {/* Add the preset buttons with margin bottom */}
                <div className="grid grid-cols-4 gap-2 mb-4">
                  <button
                    onClick={() => handleRainPresetClick(1)}
                    className={`py-2 px-4 rounded-md transition-colors ${
                      selectedRainPreset === 1 ? 'bg-blue-300' : 'bg-gray-300'
                    } hover:bg-blue-200`}
                  >
                    👨‍🌾
                  </button>
                  <button
                    onClick={() => handleRainPresetClick(2)}
                    className={`py-2 px-4 rounded-md transition-colors ${
                      selectedRainPreset === 2 ? 'bg-blue-300' : 'bg-gray-300'
                    } hover:bg-blue-200`}
                  >
                    🐸
                  </button>
                  <button
                    onClick={() => handleRainPresetClick(3)}
                    className={`py-2 px-4 rounded-md transition-colors ${
                      selectedRainPreset === 3 ? 'bg-blue-300' : 'bg-gray-300'
                    } hover:bg-blue-200`}
                  >
                    🤝
                  </button>
                  <button
                    onClick={() => handleRainPresetClick(4)}
                    className={`py-2 px-4 rounded-md transition-colors ${
                      selectedRainPreset === 4 ? 'bg-blue-300' : 'bg-gray-300'
                    } hover:bg-blue-200`}
                  >
                    ❓
                  </button>
                </div>
              </div>

              {/* Burn Section */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-4">  {/* Added mb-4 for gap below input */}
                  <h3 className="text-xl font-bold text-black">Burn</h3>
                  <div className="flex-grow mx-4">
                    <input
                      type="text"
                      value={burnAmount.toString()}
                      onChange={(e) => setBurnAmount(Math.floor(Number(e.target.value)))}
                      onBlur={(e) => {
                        const value = Math.max(1000, Math.floor(Number(e.target.value)));
                        setBurnAmount(value);
                      }}
                      className="w-full p-2 rounded border text-center font-bold"
                      min={1000}
                      step={1000}
                      placeholder="min 1,000"
                    />
                  </div>
                  <button
                    onClick={handleBurn}
                    disabled={!walletAddress || burnAmount < 1000 || isBurning}
                    className="bg-red-500 p-2 rounded-md flex items-center justify-center hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer"
                    style={{ width: '40px', height: '40px' }}
                  >
                    <span role="img" aria-label="fire" className="cursor-pointer">🔥</span>
                  </button>
                </div>

                {/* Progress Bar */}
                <div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-red-500 h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min((lottoProgress / LOTTO_GOAL) * 100, 100)}%` }}
                    ></div>
                  </div>
                  <div className="text-sm text-black text-center mt-2">  {/* Changed from text-gray-600 */}
                    <span className="font-bold text-black">
                      {lottoProgress >= LOTTO_GOAL ? (
                        <span className="text-red-500">GOAL ACHIEVED!</span>
                      ) : (
                        `${Math.ceil(lottoProgress).toLocaleString()} / ${LOTTO_GOAL.toLocaleString()} for 40 mil rain!`
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Lotto Section */}
              <div className="mt-4">
                <div className="bg-purple-500/40 rounded-xl p-4">
                  <div className="flex items-start mb-4">
                    <img src="/kingfroggo.png" alt="King Froggo" className="h-auto w-auto" />
                    <div className="flex flex-col ml-2">
                      <h3 className="text-xl font-bold text-black">Froggo Lotto Prize</h3>
                      <div className="text-3xl font-bold text-black mt-2">
                        {lottoPrize.toLocaleString()}
                      </div>
                    </div>
                  </div>

                  {/* Rest of lotto content */}
                  <div className="text-center text-black mb-1 text-sm">  {/* Changed from text-gray-600 */}
                    Your Odds of Winning: {userLottoTickets > 0 ? 
                      <span className="font-bold">{((userLottoTickets / totalLottoTicketsSold) * 100).toFixed(2)}%</span> : 
                      <span className="font-bold">0%</span>
                    }
                  </div>
                  <div className="text-center text-black text-sm">
                    Ticket Cost:{' '}
                    {froggoPrice === null ? (
                      <span>Loading...</span>
                    ) : (
                      <>
                        <span className="font-bold">
                          {Math.floor((LOTTO_TICKET_ALGO_PRICE / froggoPrice) / 2) * 2 * lottoTickets} $FROGGO 
                        </span>
                        <span className="text-sm"> ({(LOTTO_TICKET_ALGO_PRICE * lottoTickets).toFixed(2)} ALGO)</span>
                      </>
                    )}
                  </div>

                  <div className="flex flex-col items-center mt-4">
                    <div className="flex justify-center items-center space-x-2 mb-2">
                      <span className="text-black">Tickets:</span>
                      <input
                        type="text"
                        value={lottoTickets.toString()}
                        onChange={(e) => {
                          const val = e.target.value.replace(/^0+/, '');
                          setLottoTickets(val === '' ? 0 : Number(val));
                        }}
                        onBlur={(e) => {
                          const value = Math.max(1, Number(e.target.value));
                          setLottoTickets(value);
                        }}
                        className="p-2 rounded border text-center"
                        style={{ width: '80px' }}
                      />
                      <button
                        onClick={handleLottoBoostClick}
                        className={`${getLottoBoostColor()} p-2 rounded-md flex items-center transition-colors duration-200 hover:opacity-90 cursor-pointer`}
                      >
                        <span role="img" aria-label="rocket" className="cursor-pointer">🚀</span>
                      </button>
                      <span className="text-gray-600 font-bold">
                        Boost {lottoBoostMultiplier.toFixed(1)}x
                      </span>
                    </div>
                    {lottoBoostMultiplier > 1.0 && (
                      <div className="text-sm text-black mt-1 text-center">
                        Support app development with <span className="font-bold text-black">{LOTTO_BOOST_PRICES[lottoBoostMultiplier as keyof typeof LOTTO_BOOST_PRICES]} ALGO per ticket</span> and enjoy <span className="font-bold">{Math.ceil(lottoTickets * (lottoBoostMultiplier - 1))} bonus tickets!</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="text-center text-black font-bold mt-4">
                {lottoEnded ? "Lotto Ended" : `Time Remaining: ${lottoTimeRemaining}`}
              </div>
            </div>

            {/* Enter Lotto Button */}
            <button
              onClick={() => enterLotto(lottoTickets)}
              disabled={!walletAddress || lottoEnded || isGettingLottoTickets}
              className="w-full py-3 mt-4 bg-purple-500 hover:bg-purple-600 text-white font-bold rounded-xl disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isGettingLottoTickets ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing transaction...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <span role="img" aria-label="sparkles" className="mr-2">✨</span>
                  Enter Lotto
                  <span role="img" aria-label="sparkles" className="ml-2">✨</span>
                </span>
              )}
            </button>
          </div>

          {/* Raffle Box Container */}
          <div className="relative flex flex-col items-center w-full max-w-[384px]">
            {/* Raffle Box */}
            <div className="backdrop-blur-lg bg-white/30 rounded-xl p-8 w-full min-h-[600px] mb-4 shadow-[0_0_15px_rgba(255,255,255,0.8)]" style={{ 
              background: 'rgba(255, 255, 255, 0.6)',
              boxShadow: '0 0 15px rgba(255, 255, 255, 0.3), inset 0 0 15px rgba(255, 255, 255, 0.1)'
            }}>
              <div className="flex flex-col h-full">
                <div
                  className="w-full aspect-square rounded-2xl overflow-hidden mb-4"
                  style={{
                    backgroundColor: 'rgba(248, 248, 248, 0.5)',
                    maxHeight: '300px',
                    margin: '0 auto'
                  }}
                >
                  <img
                    src="/preview.jpg"
                    alt="NFT"
                    className="w-full h-full rounded-2xl"
                    style={{
                      objectFit: 'fill'
                    }}
                  />
                </div>

                <div className="flex flex-col justify-between flex-grow">
                  <div className="text-center text-xl font-bold text-gray-800 mb-4">
                    {CURRENT_RAFFLE_NAME}
                  </div>

                  <div className="text-center text-black mb-4">
                    Tickets Sold: <span className="font-bold">{totalTicketsSold.toLocaleString()}</span>
                    <br />
                    Your Tickets: <span className="font-bold">{userTickets.toLocaleString()}</span>
                    {totalTicketsSold > 0 && (
                      <span className="font-bold">
                        {' '}({((userTickets / totalTicketsSold) * 100).toFixed(2)}%)
                      </span>
                    )}
                  </div>

                  <div className="text-center text-black text-sm mb-4">
                    Ticket Cost:{' '}
                    {froggoPrice === null ? (
                      <span>Loading...</span>
                    ) : (
                      <>
                        <span className="font-bold">
                          {(roundedFroggoPerTicket ? roundedFroggoPerTicket * raffleTickets : 0).toLocaleString()} $FROGGO 
                        </span>
                        <span className="text-sm"> ({(TICKET_PRICE_IN_ALGO * raffleTickets).toFixed(2)} ALGO)</span>
                      </>
                    )}
                  </div>

                  <div className="flex flex-col items-center mb-4">
                    <div className="flex justify-center items-center space-x-2 mb-2">
                      <span className="text-black">Tickets:</span>
                      <input
                        type="text"
                        value={raffleTickets.toString()}
                        onChange={(e) => {
                          const val = e.target.value.replace(/^0+/, '');
                          setRaffleTickets(val === '' ? 0 : Number(val));
                        }}
                        onBlur={(e) => {
                          const value = Math.max(1, Number(e.target.value));
                          setRaffleTickets(value);
                        }}
                        className="p-2 rounded border text-center"
                        style={{ width: '100px' }}
                      />
                      <button
                        onClick={handleBoostClick}
                        className={`${getBoostColor()} p-2 rounded-md flex items-center transition-colors duration-200 hover:opacity-90 cursor-pointer`}
                      >
                        <span role="img" aria-label="rocket" className="mr-1 cursor-pointer">🚀</span>
                      </button>
                      <span className="text-gray-600 font-bold">
                        Boost {boostMultiplier.toFixed(1)}x
                      </span>
                    </div>
                    {boostMultiplier > 1.0 && (
                      <div className="text-sm text-black mt-1 text-center">
                        Support future raffles and development with <span className="font-bold text-black">{getBoostAlgoPrice()} ALGO per ticket</span> and enjoy <span className="font-bold">{Math.ceil(raffleTickets * (boostMultiplier - 1))} bonus tickets!</span>
                      </div>
                    )}
                  </div>

                  <div className="text-center text-black font-bold">
                    {raffleEnded ? "Raffle Ended" : `Time Remaining: ${timeRemaining}`}
                  </div>
                </div>
              </div>
            </div>
            
            {/* Get Tickets Button */}
            <button
              onClick={() => enterRaffle(raffleTickets)}
              disabled={!walletAddress || raffleEnded || isGettingRaffleTickets}
              className="w-full py-3 mt-4 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isGettingRaffleTickets ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing transaction...
                </span>
              ) : (
                'Get Tickets'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Burned Froggo */}
      <div className="-mt-8 text-center px-4">
        <div className="glass-effect rounded-xl p-4">
          <div className="text-white font-bold text-lg">
            {Math.floor(burnedFroggo).toLocaleString()} $FROGGO burned
          </div>
          <div className="text-white text-sm mb-2">
            {burnPercentage}% of total
          </div>
          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-red-600 h-2.5 rounded-full transition-all duration-500" 
              style={{ width: `${burnPercentage}%` }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;